import { useContext, useMemo, useState } from 'react'
import { ModalContext } from '../ModalContext'
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'

import BrandColorButton from 'components/Buttons/BrandColorButton'

const WEEKDAYS = [
    { ISOWeekdayIndex: 0, label: 'Monday' },
    { ISOWeekdayIndex: 1, label: 'Tuesday' },
    { ISOWeekdayIndex: 2, label: 'Wednesday' },
    { ISOWeekdayIndex: 3, label: 'Thursday' },
    { ISOWeekdayIndex: 4, label: 'Friday' },
    { ISOWeekdayIndex: 5, label: 'Saturday' },
    { ISOWeekdayIndex: 6, label: 'Sunday' },
]

export default function WorkoutConfigWeekdayAssignmentsModal() {
    
    const { modalData, HideModal } = useContext(ModalContext)

    const workoutConfigs = useMemo(() => {
        return [...(modalData.workoutConfigs ?? [])].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        )
    }, [modalData.workoutConfigs])

    const workoutConfigsById = useMemo(() => {
        return new Map(
            workoutConfigs.map((workoutConfig) => [
                workoutConfig.workoutConfigId,
                workoutConfig,
            ])
        )
    }, [workoutConfigs])

    const [workoutConfigWeekdayAssignments, setWorkoutConfigWeekdayAssignments] = useState(() => [...(modalData.workoutConfigWeekdayAssignmentsJSON ?? [])])
    const [savingChanges, setSavingChanges] = useState(false)

    const GetAssignmentCountForConfig = (workoutConfigId) => {
        return workoutConfigWeekdayAssignments.filter(
            (assignment) => assignment.workoutConfigId === workoutConfigId
        ).length
    }

    const GetAssignableWorkoutConfigs = () => {
        return workoutConfigs.filter((workoutConfig) => {
            const assignmentCount = GetAssignmentCountForConfig(workoutConfig.workoutConfigId)
            const maximumAssignmentsPerWeek = workoutConfig.maximumAssignmentsPerWeek ?? 0
            return assignmentCount < maximumAssignmentsPerWeek
        })
    }

    const GetAssignmentsForDay = (ISOWeekdayIndex) => {
        return workoutConfigWeekdayAssignments
            .filter((assignment) => assignment.ISOWeekdayIndex === ISOWeekdayIndex)
            .sort((a, b) => {
                const nameA = workoutConfigsById.get(a.workoutConfigId)?.name ?? ''
                const nameB = workoutConfigsById.get(b.workoutConfigId)?.name ?? ''
                return nameA.localeCompare(nameB)
            })
    }

    const AddAssignment = (ISOWeekdayIndex, workoutConfigId) => {
        if (!workoutConfigId) return

        const workoutConfig = workoutConfigs.find((config) => config.workoutConfigId === workoutConfigId)
        if (!workoutConfig) return

        const assignmentCount = GetAssignmentCountForConfig(workoutConfigId)
        const maximumAssignmentsPerWeek = workoutConfig.maximumAssignmentsPerWeek ?? 0

        if (assignmentCount >= maximumAssignmentsPerWeek) return

        setWorkoutConfigWeekdayAssignments((prev) => [
            ...prev,
            {
                programId: workoutConfig.programId,
                workoutConfigId: workoutConfig.workoutConfigId,
                ISOWeekdayIndex,
            },
        ])
    }

    const RemoveAssignment = (assignmentToRemove) => {
        setWorkoutConfigWeekdayAssignments((prev) =>
            prev.filter((assignment) => assignment !== assignmentToRemove)
        )
    }

    const OnSaveWorkoutConfigWeekdayAssignments = async () => {
        setSavingChanges(true)
        await modalData.buttonAction(workoutConfigWeekdayAssignments)
        setSavingChanges(false)
        HideModal()
    }

    const assignableWorkoutConfigs = GetAssignableWorkoutConfigs()

    return (
        <>
            <div className="text-center text-lg font-semibold leading-6 text-gray-900">
                Workout Schedule
            </div>

            <div className="flex flex-wrap justify-center gap-2">
                {workoutConfigs.map((workoutConfig) => {
                    const assignmentCount = GetAssignmentCountForConfig(workoutConfig.workoutConfigId)
                    const maximumAssignmentsPerWeek = workoutConfig.maximumAssignmentsPerWeek ?? 0
                    const isAtMaximum = assignmentCount >= maximumAssignmentsPerWeek

                    return (
                        <div
                            key={workoutConfig.workoutConfigId}
                            className={`
                                inline-flex items-center gap-x-1.5 rounded-md border px-2.5 py-1
                                ${isAtMaximum ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-gray-50'}
                            `}
                        >
                            <p className="text-xs font-medium text-gray-800">
                                {workoutConfig.name}
                            </p>
                            <p className={`text-xs tabular-nums ${isAtMaximum ? 'text-orange-700 font-semibold' : 'text-gray-500'}`}>
                                {assignmentCount}/{maximumAssignmentsPerWeek}
                            </p>
                        </div>
                    )
                })}
            </div>

            <div className="flex justify-center gap-x-1.5 pb-1">
                {WEEKDAYS.map((weekday) => {
                    const count = GetAssignmentsForDay(weekday.ISOWeekdayIndex).length

                    return (
                        <div
                            key={weekday.ISOWeekdayIndex}
                            className="flex w-9 flex-col items-center gap-y-1"
                        >
                            <p className="text-[10px] font-medium text-gray-500">
                                {weekday.label.slice(0, 1)}
                            </p>
                            <div
                                className={`
                                    flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold
                                    ${count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}
                                `}
                            >
                                {count}
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                {WEEKDAYS.map((weekday) => {
                    const dayAssignments = GetAssignmentsForDay(weekday.ISOWeekdayIndex)

                    return (
                        <div
                            key={weekday.ISOWeekdayIndex}
                            className="flex flex-col gap-y-2 px-3 py-3 sm:flex-row sm:items-center sm:gap-x-4"
                        >
                            <p className="w-24 shrink-0 text-sm font-semibold text-left text-gray-900">
                                {weekday.label}
                            </p>

                            <div className="flex min-h-7 min-w-0 flex-1 flex-wrap items-center gap-2">
                                {dayAssignments.length === 0 && (
                                    <p className="text-xs text-gray-400">Rest day</p>
                                )}

                                {dayAssignments.map((assignment, index) => (
                                    <div
                                        key={assignment.workoutConfigWeekdayAssignmentId ?? `${assignment.workoutConfigId}-${index}`}
                                        className="inline-flex items-center gap-x-1.5 rounded-md border border-gray-300 bg-white pl-2.5 pr-1 py-1"
                                    >
                                        <p className="text-xs text-gray-800">
                                            {workoutConfigsById.get(assignment.workoutConfigId)?.name ?? 'Unknown workout'}
                                        </p>

                                        <button
                                            type="button"
                                            className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                            onClick={() => RemoveAssignment(assignment)}
                                        >
                                            <span className="sr-only">Remove</span>
                                            <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                        </button>
                                    </div>
                                ))}

                                <div className="flex-1" />

                                <div
                                    className={`relative h-7 w-[60px] shrink-0 ${
                                        assignableWorkoutConfigs.length === 0 ? 'invisible' : ''
                                    }`}
                                    aria-hidden={assignableWorkoutConfigs.length === 0}
                                >
                                    <PlusIcon
                                        className="pointer-events-none absolute left-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                                        aria-hidden="true"
                                    />

                                    <select
                                        className="h-7 w-full appearance-none rounded-md border border-dashed border-gray-300 bg-white pl-6 pr-1 text-xs text-gray-600 hover:border-gray-400 hover:text-gray-800"
                                        value=""
                                        disabled={assignableWorkoutConfigs.length === 0}
                                        tabIndex={assignableWorkoutConfigs.length === 0 ? -1 : 0}
                                        onChange={(e) => AddAssignment(weekday.ISOWeekdayIndex, e.target.value)}
                                    >
                                        <option value="" disabled>
                                            Add
                                        </option>

                                        {assignableWorkoutConfigs.map((workoutConfig) => (
                                            <option
                                                key={workoutConfig.workoutConfigId}
                                                value={workoutConfig.workoutConfigId}
                                            >
                                                {workoutConfig.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className="pt-2">
                <BrandColorButton
                    text="Save"
                    action={OnSaveWorkoutConfigWeekdayAssignments}
                    isLoading={savingChanges}
                />
            </div>
        </>
    )
}
